/**
 * Email Composer Styles
 *
 * Default styling for composed emails.
 * Merchants can override these via workspace configuration.
 *
 * @module email-composer/styles
 */

/**
 * Get default email styles — the Daylight brand kit: the SAME tokens the chat
 * widget renders on light storefronts (.mv-light), with the button shade
 * darkened for email's fixed white text contrast. (After Hours dark stays
 * the widget's look on dark storefronts and Marva's own transactional mail.)
 * @returns {Object} Default style configuration
 */
function getDefaultStyles() {
  return {
    // Colors (Daylight — the light-warm sibling of the widget's .mv-light block)
    // #B68A3E is the Daylight brand fact, but white text on it is only 3.1:1.
    // The shared resolver derives this contrast-safe button shade; the raw
    // fallback must use the same safe token when no workspace facts exist.
    primaryColor: '#947033',
    accentColor: '#586647', // Sage ink - discounts, savings
    textColor: '#2A2620', // Warm ink - primary text
    secondaryTextColor: '#6B6256', // Soft ink - secondary text
    backgroundColor: '#EFEAE1', // Warm paper - email outer background
    contentBackgroundColor: '#FCFAF6', // Surface - email content container
    buttonTextColor: '#ffffff', // White on gold (matches .mv-light .mv-btn.gold)

    // Typography
    fontFamily: '\'Hanken Grotesk\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif',
    headingFontSize: '24px',
    bodyFontSize: '16px',
    smallFontSize: '14px',

    // Spacing
    containerPadding: '32px',
    sectionGap: '24px',
    cardBorderRadius: '8px',
    buttonBorderRadius: '8px',

    // Button style
    buttonStyle: 'filled', // 'filled' or 'outline'

    // Layout
    maxWidth: '600px'
  };
}

/**
 * Generate inline CSS for a component
 * Used for elements that need inline styles for email client compatibility
 *
 * @param {Object} styles - Style configuration
 * @param {string} component - Component name ('button', 'card', 'text', etc.)
 * @returns {string} Inline CSS string
 */
function getInlineStyles(styles, component) {
  switch (component) {
    case 'button':
      if (styles.buttonStyle === 'outline') {
        return `display: inline-block; background: transparent; color: ${styles.primaryColor}; border: 2px solid ${styles.primaryColor}; padding: 12px 26px; border-radius: ${styles.buttonBorderRadius}; text-decoration: none; font-weight: 600; font-size: ${styles.bodyFontSize}; font-family: ${styles.fontFamily};`;
      }
      return `display: inline-block; background: ${styles.primaryColor}; color: ${styles.buttonTextColor || 'white'}; padding: 14px 28px; border-radius: ${styles.buttonBorderRadius}; text-decoration: none; font-weight: 600; font-size: ${styles.bodyFontSize}; font-family: ${styles.fontFamily};`;

    case 'button-secondary':
      return `display: inline-block; background: #f3f4f6; color: ${styles.textColor}; padding: 14px 28px; border-radius: ${styles.buttonBorderRadius}; text-decoration: none; font-weight: 600; font-size: ${styles.bodyFontSize}; font-family: ${styles.fontFamily};`;

    case 'card':
      return `border: 1px solid #e5e7eb; border-radius: ${styles.cardBorderRadius}; overflow: hidden; background: white;`;

    case 'heading':
      return `font-size: ${styles.headingFontSize}; font-weight: 700; color: ${styles.textColor}; margin: 0 0 16px 0; font-family: ${styles.fontFamily};`;

    case 'body':
      return `font-size: ${styles.bodyFontSize}; line-height: 1.6; color: ${styles.textColor}; font-family: ${styles.fontFamily};`;

    case 'small':
      return `font-size: ${styles.smallFontSize}; color: ${styles.secondaryTextColor}; font-family: ${styles.fontFamily};`;

    case 'discount':
      return `background: ${styles.accentColor}; color: white; padding: 12px 20px; border-radius: 8px; text-align: center; font-weight: 600; font-family: ${styles.fontFamily};`;

    case 'link':
      return `color: ${styles.primaryColor}; text-decoration: underline;`;

    default:
      return '';
  }
}

/**
 * CSS color name to hex conversion for common colors
 * Email clients don't always support color names
 */
const COLOR_MAP = {
  blue: '#2563eb',
  green: '#10b981',
  red: '#ef4444',
  orange: '#f97316',
  purple: '#8b5cf6',
  pink: '#ec4899',
  gray: '#6b7280',
  black: '#1f2937',
  white: '#ffffff'
};

/**
 * Normalize color value to hex
 * @param {string} color - Color value (name, hex, or rgb)
 * @returns {string} Hex color value
 */
function normalizeColor(color) {
  if (!color) return null;

  // Already hex
  if (color.startsWith('#')) {
    return color;
  }

  // Color name
  if (COLOR_MAP[color.toLowerCase()]) {
    return COLOR_MAP[color.toLowerCase()];
  }

  // Return as-is for rgb/rgba
  return color;
}

module.exports = {
  getDefaultStyles,
  getInlineStyles,
  normalizeColor,
  COLOR_MAP
};
